import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Country, CountryService } from '@shared/services/country.service';
import {
  ActionEnum,
  Cause,
  LogEntry,
  LogFiltersType,
  LogRequest,
  LogsService,
} from '@shared/services/logs/logs.service';
import { BANDWIDTH, GraphType, RESPONSE_TIME, TRAFIC } from '@shared/components/highcharts/graph/graph';
import { SiteSelectorComponent } from '@shared/components/site-selector/site-selector.component';
import { ToastrService } from '@shared/services/toastr.service';
import { DateRange } from '@shared/utils/date-range';
import { EmptyError, firstValueFrom, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { FiltersComponent } from './filters/filters.component';
import { MyLogsGraphsComponent } from './graphs/my-logs-graphs.component';
import { Filter, Filters, FiltersEnum, formatFiltersLogs } from './filters/filters';
import _ from 'lodash';
import { TrackEvent } from '../dashboard/track.event';
import { BACKEND_PREFIX } from '../../app-routing.constants';
import { ADMIN_CONTEXT, CONTEXT, ContextEnum } from '@shared/models/ContextSiteModel';
import { SitesService } from '@shared/services/sites/sites.service';
import { BaseQueryParamsShape, QueryParamsService } from '@shared/services/query-params-service/query-params.service';
import { SpringPage } from '@shared/page';
import { SiteSelectorComponent as SiteSelectorComponent_1 } from '../../shared/components/site-selector/site-selector.component';
import { DecimalPipe, NgClass, NgFor, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { TypedTemplateDirective } from '@shared/directives/typed-template.directive';
import { JsonViewer } from '@shared/components/json-viewer/json-viewer.component';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import {
  DateFormatPipe,
  DurationPipe,
  NumberFormatPipe,
  StripHtmlPipe,
  TranslateLabelPipe,
} from '@shared/pipe/filter.pipe';
import * as pager from '@shared/pager';
import { formatHumanReadableDate } from '@shared/utils/data-utils';

type QueryParamsShape = BaseQueryParamsShape & {
  filters: string | null;
};

@Component({
  selector: 'app-my-logs',
  templateUrl: './my-logs.component.html',
  styleUrls: ['./my-logs.component.scss'],
  imports: [
    SiteSelectorComponent_1,
    FiltersComponent,
    MyLogsGraphsComponent,
    NgIf,
    NgxDatatableModule,
    TypedTemplateDirective,
    JsonViewer,
    NgFor,
    HelpComponent,
    NgTemplateOutlet,
    NgbTooltip,
    NgStyle,
    NgClass,
    FormsModule,
    DecimalPipe,
    DateFormatPipe,
    DurationPipe,
    NumberFormatPipe,
    TranslatePipe,
    StripHtmlPipe,
    TranslateLabelPipe,
  ],
})
export class MyLogsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('siteSelector') siteSelector!: SiteSelectorComponent;
  @ViewChild('filtersComponent') filtersComponent: FiltersComponent;
  @ViewChild('graphsComponent') graphsComponent!: MyLogsGraphsComponent;
  @ViewChild('progressBar', { static: true }) progressBar: ElementRef;
  @ViewChild('logsTable') logsTable;

  public countries: Country[];
  LogFiltersType = LogFiltersType;
  lang: string;
  ctx: ContextEnum = CONTEXT;
  ADMIN_CONTEXT = ADMIN_CONTEXT;
  logs: LogEntryVM[] = [];
  RESPONSE_TIME = RESPONSE_TIME;
  BANDWIDTH = BANDWIDTH;
  FiltersEnum = FiltersEnum;
  ActionEnum = ActionEnum;
  filters: Filter[] = [];
  activeFilters: Filter[] = [];
  total: number = null;
  size: number = 20;
  index: number = 0;
  graphType: GraphType = TRAFIC;
  isLoading: boolean = false;
  logsTableMustScroll: boolean = false;

  protected CAUSES_CREDIBILITY_CLASS: { [prop in Cause]: string } = {
    analyzedOK: null,
    passthroughRule: 'exception',
    whitelistedIp: 'exception',
    blacklistedIp: 'exception',
    blacklistedIpCluster: 'exception',
    urlException: 'exception',
    analyzedKO: null,
    geoBlocked: 'ban',
    blockedByRule: 'exception',
    banned: 'ban',
    cdnHit: 'exception',
    cdnBlocked: 'ban',
    challenge: null,
    challengeEmitted: null,
    challengeFailed: null,
    challengeSucceeded: null,
    acmeChallenge: null,
  };

  protected CAUSES_SHOW_CREDIBILITY_BAR: { [prop in Cause]: boolean } = {
    analyzedOK: true,
    passthroughRule: true,
    whitelistedIp: false,
    blacklistedIp: false,
    blacklistedIpCluster: false,
    urlException: false,
    analyzedKO: true,
    geoBlocked: true,
    blockedByRule: true,
    banned: true,
    cdnHit: true,
    cdnBlocked: true,
    challenge: false,
    challengeEmitted: true,
    challengeFailed: false,
    challengeSucceeded: false,
    acmeChallenge: false,
  };

  protected readonly Math = Math;

  private navState?: TrackEvent;
  private subscriptions: Subscription = new Subscription();
  private hasInitialized = false;

  rowType = { row: {} as LogEntryVM, expanded: true as boolean, rowIndex: 0 };

  constructor(
    private logsService: LogsService,
    private queryParamsService: QueryParamsService,
    private auth: AuthService,
    public sites: SitesService,
    private countryService: CountryService,
    public translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
  ) {
    this.lang = this.auth.getCurrentLanguage();

    if (this.route.snapshot.data.adminClusterView) {
      this.ctx = ADMIN_CONTEXT;
    }
    this.navState = this.router.getCurrentNavigation()?.extras?.state as TrackEvent;
  }

  async ngOnInit() {
    await this.countryService.init();
    this.countries = this.countryService.getCountries();
  }

  async ngAfterViewInit() {
    await this.sites.load(this.ctx);

    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    this.subscriptions.add(this.route.queryParamMap.subscribe((params) => this.onUrlChange(params)));

    this.subscriptions.add(
      this.breakpointObserver
        .observe(['(max-width: 1395px)'])
        .subscribe((result) => (this.logsTableMustScroll = result.matches)),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onSiteSelectionChanged() {
    this.setQueryParams();
    this.refresh();
  }

  onPeriodEmitted(range: DateRange) {
    this.sites[this.ctx].current.period.range = range;
    this.setQueryParams();
    this.refresh();
  }

  onPeriodEmittedFromGraph(range: DateRange) {
    this.sites[this.ctx].current.period.autoRefresh = false;
    this.onPeriodEmitted(range);
  }

  onFiltersChange(filters: Filter[]) {
    this.index = 0;
    this.filters = filters;
    this.updateActiveFilters();
    this.queryParamsService.storeFilters(this.ctx, this.queryParamsService.serializeFiltersToUrl(this.filters));
    this.setQueryParams();
    this.cdr.detectChanges();
    this.refresh();
  }

  async loadLogsPage() {
    if (this.isLoading || !this.sites[this.ctx].current.cluster?.uid) return;

    this.isLoading = true;
    try {
      const page = await firstValueFrom(
        this.logsService.getLogs(this.getLogRequest(), { page: this.index, size: this.size }, this.ctx).pipe(
          catchError((err) => {
            if (!(err instanceof EmptyError)) {
              console.error('getLogs failed', err);
              this.toastr.error('LogsLoadError');
            }
            return of({ totalElements: 0, content: [] } as SpringPage<LogEntry>);
          }),
        ),
      );
      this.siteSelector.countNextRefresh = 0;
      this.total = page.totalElements;
      this.logs = page.content.map((log) => ({
        ...log,
        countryName: this.countryService.getCountryName(log.countryCode),
        requestDetails: {
          date: formatHumanReadableDate(log.date, this.auth.getCurrentLanguage(), 'longMs'),
          ..._(log.requestDetails).toPairs().sortBy(0).fromPairs().value(),
        },
        causeLabel:
          log.mainAttack?.name && log.cause !== 'challengeEmitted'
            ? log.mainAttack?.name
            : log.cause
              ? this.translate.instant('Cause.' + log.cause)
              : null,
      }));
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  async filterSite(siteName: string) {
    this.index = 0;
    this.sites.setSites(this.ctx, [siteName]);
    this.setQueryParams();
    this.refresh();
  }

  filter(type: LogFiltersType | null, slug: FiltersEnum, value: any, label?: string) {
    if (slug === ('site' as FiltersEnum)) return this.filterSite(value);
    this.createFilter(type, slug, value, label);
  }

  private async onUrlChange(params: ParamMap): Promise<void> {
    if (this.hasInitialized && this.queryParamsService.paramsMatchCurrentState(params, this.buildQueryParams())) {
      return;
    }

    const filtersParam = params.get('filters');

    if (filtersParam) {
      this.filters = await this.queryParamsService.deserializeFiltersFromUrl(filtersParam, this.filtersComponent);
      this.queryParamsService.storeFilters(this.ctx, filtersParam);
    } else if (!this.navState) {
      const stored = this.queryParamsService.getStoredFilters(this.ctx);
      this.filters = stored
        ? await this.queryParamsService.deserializeFiltersFromUrl(stored, this.filtersComponent)
        : [];
    } else {
      this.filters = [];
    }

    this.updateActiveFilters();
    this.syncFiltersToComponent();

    const hasBaseParams = this.queryParamsService.applyQueryParams(params, this.ctx, !!filtersParam, !this.navState);

    if (!this.hasInitialized) {
      if (!hasBaseParams && !filtersParam && this.navState) {
        await this.applyNavState(this.navState);
        this.navState = undefined;
      }
      this.setQueryParams(true);
    }

    this.hasInitialized = true;
    this.refresh();
  }

  loadData() {
    if (!this.sites[this.ctx].current.cluster?.uid) {
      this.graphsComponent.noSitesFallback();
      return;
    }

    this.index = 0;

    // Force la propagation des @Input (notamment `filters`) vers graphsComponent
    // avant d'appeler loadGraphs, sinon le graph utilise une valeur obsolète.
    this.cdr.detectChanges();

    const p1 = this.loadLogsPage();
    this.graphsComponent.resetData();
    const p2 = this.graphsComponent.loadGraphs();

    Promise.all([p1, p2]).then(() => {
      if (this.sites[this.ctx].current.period.autoRefresh) this.siteSelector.onDataLoaded();
      else this.siteSelector.clearRefreshInterval();
    });
  }

  private buildQueryParams(): QueryParamsShape {
    return {
      ...this.queryParamsService.buildQueryParams(this.ctx),
      filters: this.queryParamsService.serializeFiltersToUrl(this.filters),
    };
  }

  refresh() {
    this.index = 0;
    this.sites[this.ctx].refreshPeriod();
    this.queryParamsService.storeState(this.ctx, this.queryParamsService.buildQueryParams(this.ctx));
    this.loadData();
  }

  private setQueryParams(replaceUrl = false) {
    const queryParams = this.buildQueryParams();
    this.queryParamsService.storeState(this.ctx, queryParams);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl,
      queryParamsHandling: 'merge',
    });
  }

  private updateActiveFilters(): void {
    this.activeFilters = this.filters.filter((f) => f.active);
  }

  private getLogRequest(): LogRequest {
    return { ...this.sites[this.ctx].buildRequestData(), ...formatFiltersLogs(this.activeFilters) };
  }

  downloadLogs() {
    this.toastr.info('DownloadInProgress');
    const link = document.getElementById('downloadAnchorElem') as HTMLAnchorElement;
    link.href = BACKEND_PREFIX + this.logsService.downloadLogsURL(this.getLogRequest(), this.ctx);
    link.click();
  }

  async createFiltersFromEvent(event: LogQueryParam) {
    // filters from events is necessarily filter in
    let key: keyof LogQueryParam;
    for (key in event) {
      if (!event[key]) continue;
      await this.filtersComponent.createFilter(LogFiltersType.IN, key, event[key]);
    }
    return this.filtersComponent.emitFilters();
  }

  async createFilter(
    type: LogFiltersType | null,
    key: FiltersEnum,
    value: string | number,
    label?: string,
  ): Promise<void> {
    const shouldRefresh = await this.filtersComponent.createFilter(type, key, value, label);
    if (shouldRefresh) return this.filtersComponent.emitFilters();
  }

  private async applyNavState(state: TrackEvent) {
    const { sites, clusterName, graphType, graphSubType, ...rest } = state;

    if (clusterName) this.sites.resetContext(this.ctx, clusterName);
    if (sites) this.sites.setSites(this.ctx, sites);

    const filterParams = _.pickBy(rest, (_v, k) => Filters.includes(k));
    if (Object.keys(filterParams).length) {
      await this.createFiltersFromEvent(filterParams);
    }
  }

  toggleExpandRow(row: LogEntryVM) {
    this.logsTable.rowDetail.toggleExpandRow(row);
  }

  changePage(evt: any) {
    this.index = evt.page - 1;
    this.loadLogsPage();
  }

  private syncFiltersToComponent() {
    if (!this.filtersComponent) return;
    this.filtersComponent.filters = this.filters.reduce((acc, f) => acc.set(f.filterDefinition.key, f), new Map());
  }

  canFilterOut(slug): boolean {
    return (
      slug != ('site' as FiltersEnum) && slug != FiltersEnum.RESPONSE_TIME_GTE && slug != FiltersEnum.CONTENT_LENGTH_GTE
    );
  }

  appendAggravatingFactorDesc(driveLabel: string): string {
    if (driveLabel.includes('Aggravating factor') || driveLabel.includes('Facteur aggravant')) {
      return this.translate.instant('inCaseOfOtherDetectedAnomalies');
    } else {
      return '';
    }
  }

  protected readonly pager = pager;
}

type LogQueryParam = Partial<{ [k in FiltersEnum]: any }>;

type LogEntryVM = LogEntry & { countryName?: string; causeLabel?: string };
